import React from 'react';
import { FerrisWheel as Ferris, Menu, X } from 'lucide-react';

function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('home');

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      // Update active section based on scroll position
      const sections = ['home', 'servizi', 'galleria', 'contatti'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (current) {
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Calculate offset considering the fixed navbar
      const navbarHeight = 64; // 4rem/16px = 64px
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      // Smooth scroll with proper offset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // Update active section
      setActiveSection(sectionId);
      
      // Close mobile menu
      setIsOpen(false);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  };

  return (
    <nav className={`fixed w-full z-[90] transition-all duration-500 navbar ${
      scrolled ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-pink-100/50' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center group relative">
            <div className="relative z-10 transform hover:scale-110 transition-all duration-1000 group perspective-1000">
              <Ferris className={`h-12 w-12 transform animate-spin-slow hover:scale-110 transition-all duration-700 ${
                scrolled ? 'text-pink-600' : 'text-white'
              } group-hover:animate-spin-faster`} />
              <div className="absolute inset-0 bg-gradient-to-r from-pink-400/30 to-purple-400/30 blur-2xl rounded-full animate-pulse group-hover:animate-pulse-fast"></div>
              <div className="absolute -inset-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 blur-xl rounded-full animate-spin-reverse-slow group-hover:scale-150 group-hover:rotate-180 transition-all duration-1000"></div>
              <div className="absolute -inset-2 bg-gradient-to-r from-pink-300/10 to-purple-300/10 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-ping-slow"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-400/10 to-purple-400/10 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 animate-morph"></div>
            </div>
            <span className={`ml-3 text-3xl font-black text-transparent bg-clip-text transition-all duration-700 animate-gradient ${
              scrolled 
                ? 'bg-gradient-to-r from-pink-600 via-pink-500 to-purple-500 group-hover:from-purple-500 group-hover:via-pink-500 group-hover:to-pink-600'
                : 'bg-gradient-to-r from-white via-pink-100 to-purple-200 group-hover:from-purple-200 group-hover:via-pink-100 group-hover:to-white'
            }`}>
              ValentinaEventi
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-pink-100/10 to-purple-100/10 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-morph"></div>
          </div>
          <div className="flex items-center">
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`inline-flex items-center justify-center p-2 rounded-md transition-colors duration-200 ${
                  scrolled
                    ? 'text-pink-600 hover:text-pink-700 hover:bg-pink-100'
                    : 'text-white hover:text-pink-100 hover:bg-white/10'
                }`}
              >
                <span className="sr-only">Open main menu</span>
                {isOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-6">
                {['Home', 'Servizi', 'Galleria', 'Contatti'].map((item, index) => (
                  <a 
                    key={index}
                    href={`#${item === 'Home' ? 'home' : item.toLowerCase()}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(item === 'Home' ? 'home' : item.toLowerCase());
                    }} 
                    className={`px-4 py-2 rounded-full text-sm font-semibold relative group overflow-hidden transition-all duration-500 hover:scale-105 ${
                      scrolled 
                        ? 'text-gray-900 hover:text-white hover:bg-gradient-to-r hover:from-pink-600 hover:to-purple-600' 
                        : 'text-white hover:bg-white/20'
                    } ${activeSection === (item === 'Home' ? 'home' : item.toLowerCase()) ? 
                        'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg scale-105 hover:shadow-xl' : ''}`}
                  >
                    <span className="relative z-10">{item}</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Mobile menu, show/hide based on menu state */}
        <div className={`${isOpen ? 'block' : 'hidden'} md:hidden`}>
          <div className="px-4 pt-4 pb-6 space-y-2 bg-white/95 backdrop-blur-md rounded-b-2xl shadow-lg border-t border-pink-100">
            {['Home', 'Servizi', 'Galleria', 'Contatti'].map((item, index) => (
              <a
                key={index}
                href={`#${item === 'Home' ? 'home' : item.toLowerCase()}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item === 'Home' ? 'home' : item.toLowerCase());
                }}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-500 ${
                  activeSection === (item === 'Home' ? 'home' : item.toLowerCase())
                    ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg'
                    : 'text-gray-900 hover:text-white hover:bg-gradient-to-r hover:from-pink-600 hover:to-purple-600'
                }`}
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;